import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { GlobalSearchService } from '../../services/globalsearchservice.service'
import { PrintService } from '../../services/print.service'
import { FormControl } from '@angular/forms';

@Component({
	selector: 'app-trial-balance',
	templateUrl: './trial-balance.component.html',
	styleUrls: ['./trial-balance.component.scss'],
})
export class TrialBalanceComponent implements OnInit, OnChanges, AfterViewInit {
	@Input() data: any = [];
	/** From API: full `salesman` table — keeps the dropdown populated even when rows lack a rep. */
	@Input() salesmenCatalog: { code: string; name: string }[] = [];
	/**
	 * From API: brand (`manufacturer`) catalog. Empty = tenant has no brand attribution,
	 * which is what hides the Brand column and filter everywhere but MOOV.
	 */
	@Input() brandsCatalog: { id: number; name: string }[] = [];
	/** Wallet column opens account overview (AR, cards, open docs). Used from overdue / trial-balance dashboard. */
	@Input() showAccountOverviewAction = false;
	/** Payment column opens full payment entry (`app-payment`) in a modal. */
	@Input() showPaymentAction = false;
	/** Show Refresh next to Search (e.g. overdue dashboard re-fetch). */
	@Input() showToolbarRefresh = false;
	@Input() toolbarRefreshDisabled = false;
	/**
	 * When set (e.g. `transactions`), Name / Customer # links include this `tab` query param so
	 * `/customers/view/:id` opens the right workspace (Transactions, AR edit, etc.).
	 */
	@Input() customerViewTab: string | null = null;
	@Output() accountOverview = new EventEmitter<{ debtorno: string; branchcode: string; title: string }>();
	@Output() takePayment = new EventEmitter<{ debtorno: string; branchcode: string; title: string }>();
	@Output() toolbarRefresh = new EventEmitter<void>();
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild('printTable') printTableRed: ElementRef;
	sumdet = 'summary';

	trial_balance_columns: any = [
		'name',
		'debtorno',
		'terms',
		'brands_display',
		'creditlimit',
		'reasondescription',
		'typename',
		'balance',
		'future',
		'current',
		'due',
		'overdue',
		'overdue1',
		'overdue2',
	];

	/** `true` = Summary (compact rows; use ▸ to open one customer’s lines). `false` = Detail (nested lines under every row). */
	summary_details = new FormControl<boolean>(true, { nonNullable: true });
	summary_or_details = [
		{ label: 'Summary', value: true },
		{ label: 'Detail', value: false },
	];
	show_details = false;
	private summaryRestoredFromStorage = false;

	po_columns: string[] = [
		'typename_po',
		'transno_po',
		'order__po',
		'trandate_po',
		'daysbeforedue_po',
		'payterms_po',
		'balance_po',
		'future_po',
		'current_po',
		'due_po',
		'overdue1_po',
		'overdue2_po',
		'reasondescription_po',
	];

	matdata: any
	pdf = false;
	hold_data: any = [];
	/** After structural filters (salesman, terms, card, etc.); search overlays to `gridRows`. */
	all: any = [];
	/** Input snapshot / master rows (do not assign filtered rows to `@Input() data`). */
	masterData: any = [];
	/** Rows shown in the grid (filters + search + sort). */
	gridRows: any[] = [];
	sending = false;
	pageSize = 2500;
	total_balance = 0;
	total_current = 0;
	total_due = 0;
	total_overdue1 = 0;
	total_overdue2 = 0;
	total_overdue = 0;
	total_future = 0;
	paginator_show = true;
	arlink: any = '';

	private static readonly FILTER_STORAGE_KEY = 'arv8.sharedTrialBalance.filters';

	/** Empty = no restriction (all). Otherwise row must match one of the selected codes. */
	filterSalesmanCodes: string[] = [];
	filterTermsCodes: string[] = [];
	/**
	 * Brand ids (as strings so `NO_BRAND_KEY` can share the list). Empty = all.
	 * A customer matches when ANY of their open lines carries a selected brand —
	 * amounts are never split, so the row keeps the full balance it owes.
	 */
	filterBrandIds: string[] = [];
	/** When false, rows with balance &lt; 0 are hidden. Default true = show everyone. */
	includeNegativeBalances = true;
	/** all = no restriction; yes = card_on_file Y; no = not Y */
	cardOnFileFilter: 'all' | 'yes' | 'no' = 'all';
	/** Search box — overlaid after structural filters; persisted with session filters. */
	toolbarSearchText = '';
	/** At most one customer’s AR line grid expanded below its summary row. */
	expandedDetailRowKey: string | null = null;
	salesmanOptions: { code: string; name: string }[] = [];
	termsOptions: { code: string; label: string }[] = [];
	/** Brands present on the loaded rows, plus "(No brand)" when some lines are untagged. */
	brandOptions: { code: string; label: string }[] = [];

	/** Sentinel for AR lines with no brand — receipts, and the odd unattributed import. */
	static readonly NO_BRAND_KEY = '__none__';
	readonly noBrandKey = TrialBalanceComponent.NO_BRAND_KEY;

	/** Brand filter and column only exist where the API returns a brand catalog (MOOV). */
	get brandFilterEnabled(): boolean {
		return this.brandOptions.length > 0;
	}

	/** Client-side column sort (plain HTML table, not mat-table). */
	sortColumn: string | null = null;
	sortDir: 'asc' | 'desc' = 'asc';

	private readonly sortNumericKeys = new Set([
		'creditlimit',
		'balance',
		'future',
		'current',
		'due',
		'overdue',
		'overdue1',
		'overdue2',
	]);

	/** Main grid column count (detail expand row colspan). Lines column exists only in Summary mode. */
	get trialBalanceColCount(): number {
		const linesCol = this.summary_details.value ? 1 : 0;
		return (
			linesCol +
			15 +
			(this.brandFilterEnabled ? 1 : 0) +
			(this.showAccountOverviewAction ? 1 : 0) +
			(this.showPaymentAction ? 1 : 0)
		);
	}

	/** Name + Customer # + optional action cols + descriptor cols — merged in footer so totals line up. */
	get footerLabelColspan(): number {
		let n = 2;
		if (this.showAccountOverviewAction) {
			n++;
		}
		if (this.showPaymentAction) {
			n++;
		}
		n += 6;
		if (this.brandFilterEnabled) {
			n++;
		}
		return n;
	}

	constructor(
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private datePipe: DatePipe,
	) {}

	ngOnInit(): void {
		this.loadPersistedFilters();
		this.arlink = this.globalSearchService.config;
		if (!this.summaryRestoredFromStorage) {
			this.summary_details.setValue(true, { emitEvent: false });
			this.show_details = false;
		}
		this.summary_details.valueChanges.subscribe((newvalue) => {
			this.show_details = !newvalue;
			this.expandedDetailRowKey = null;
			this.savePersistedFilters();
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['data']) {
			this.initFromInputData();
		} else if ((changes['salesmenCatalog'] || changes['brandsCatalog']) && this.masterData.length) {
			this.buildFilterOptions();
			this.applyFilters();
		}
	}

	ngAfterViewInit(): void {
		this.attachSortAndPaginator();
	}

	private initFromInputData(): void {
		const incoming = Array.isArray(this.data) ? this.data : [];
		this.masterData = incoming.map((row) => (row && typeof row === 'object' ? { ...row } : row));
		this.hold_data = this.masterData;
		this.expandedDetailRowKey = null;
		this.buildFilterOptions();
		this.pruneInvalidFilterSelections();
		this.applyFilters();
	}

	private loadPersistedFilters(): void {
		try {
			const raw = sessionStorage.getItem(TrialBalanceComponent.FILTER_STORAGE_KEY);
			if (!raw) {
				return;
			}
			const p = JSON.parse(raw) as Record<string, unknown>;
			if (Array.isArray(p['filterSalesmanCodes'])) {
				this.filterSalesmanCodes = (p['filterSalesmanCodes'] as unknown[])
					.map((c) => String(c).trim())
					.filter((c) => c !== '');
			}
			if (Array.isArray(p['filterTermsCodes'])) {
				this.filterTermsCodes = (p['filterTermsCodes'] as unknown[])
					.map((c) => String(c).trim())
					.filter((c) => c !== '');
			}
			if (Array.isArray(p['filterBrandIds'])) {
				this.filterBrandIds = (p['filterBrandIds'] as unknown[])
					.map((c) => String(c).trim())
					.filter((c) => c !== '');
			}
			if (typeof p['includeNegativeBalances'] === 'boolean') {
				this.includeNegativeBalances = p['includeNegativeBalances'];
			}
			const cof = p['cardOnFileFilter'];
			if (cof === 'all' || cof === 'yes' || cof === 'no') {
				this.cardOnFileFilter = cof;
			}
			if (p['sortColumn'] === null || typeof p['sortColumn'] === 'string') {
				this.sortColumn = p['sortColumn'] as string | null;
			}
			if (p['sortDir'] === 'asc' || p['sortDir'] === 'desc') {
				this.sortDir = p['sortDir'];
			}
			if (typeof p['summaryDetails'] === 'boolean') {
				this.summary_details.setValue(p['summaryDetails'], { emitEvent: false });
				this.show_details = !p['summaryDetails'];
				this.summaryRestoredFromStorage = true;
			}
			if (typeof p['toolbarSearchText'] === 'string') {
				this.toolbarSearchText = p['toolbarSearchText'];
			}
		} catch {
			/* ignore corrupt storage */
		}
	}

	private savePersistedFilters(): void {
		try {
			sessionStorage.setItem(
				TrialBalanceComponent.FILTER_STORAGE_KEY,
				JSON.stringify({
					filterSalesmanCodes: this.filterSalesmanCodes,
					filterTermsCodes: this.filterTermsCodes,
					filterBrandIds: this.filterBrandIds,
					includeNegativeBalances: this.includeNegativeBalances,
					cardOnFileFilter: this.cardOnFileFilter,
					sortColumn: this.sortColumn,
					sortDir: this.sortDir,
					summaryDetails: this.summary_details.value,
					toolbarSearchText: this.toolbarSearchText,
				}),
			);
		} catch {
			/* ignore quota / private mode */
		}
	}

	private pruneInvalidFilterSelections(): void {
		const salesCodes = new Set(this.salesmanOptions.map((s) => s.code));
		const termKeys = new Set(this.termsOptions.map((t) => t.code));
		const brandKeys = new Set(this.brandOptions.map((b) => b.code));
		this.filterSalesmanCodes = this.filterSalesmanCodes.filter((c) => salesCodes.has(c));
		this.filterTermsCodes = this.filterTermsCodes.filter((c) => termKeys.has(c));
		this.filterBrandIds = this.filterBrandIds.filter((c) => brandKeys.has(c));
	}

	/**
	 * Brand keys a customer can be matched on — every brand across their open lines,
	 * plus the no-brand sentinel when some line is untagged (receipts always are).
	 */
	private brandFilterKeysForCustomer(c: any): string[] {
		const out: string[] = [];
		const seen = new Set<string>();
		const add = (v: unknown) => {
			const t = v != null ? String(v).trim() : '';
			if (t && !seen.has(t)) {
				seen.add(t);
				out.push(t);
			}
		};
		if (Array.isArray(c?.details)) {
			for (const d of c.details) {
				if (!d?.name) {
					continue;
				}
				const ids = Array.isArray(d?.brand_ids) ? d.brand_ids : [];
				if (ids.length === 0) {
					add(TrialBalanceComponent.NO_BRAND_KEY);
					continue;
				}
				for (const id of ids) {
					add(id);
				}
			}
		}
		if (out.length === 0 && Array.isArray(c?.brand_ids)) {
			for (const id of c.brand_ids) {
				add(id);
			}
		}
		return out;
	}

	/** Summary column: the distinct brands owed on this customer's open lines. */
	customerBrandsSummary(row: any): string {
		const v = row?.brands_display != null ? String(row.brands_display).trim() : '';
		return v !== '' ? v : '—';
	}

	/** Detail line: brands on that one document. */
	detailBrands(det: any): string {
		const v = det?.brands_display != null ? String(det.brands_display).trim() : '';
		return v !== '' ? v : '—';
	}

	private getSalesmanCode(c: any): string {
		const v = c?.salesman ?? c?.salesmancode ?? c?.salesperson ?? '';
		return String(v).trim();
	}

	/** Terms filter key: prefer API termsindicator, else fall back to display terms (always on aged rows). */
	private getTermsFilterKey(c: any): string {
		const ti = c?.termsindicator != null ? String(c.termsindicator).trim() : '';
		if (ti) {
			return ti;
		}
		return c?.terms != null ? String(c.terms).trim() : '';
	}

	/** Distinct term codes for this customer (master + each detail line) for multi-select filter. */
	private termFilterKeysForCustomer(c: any): string[] {
		const out: string[] = [];
		const seen = new Set<string>();
		const add = (v: unknown) => {
			const t = v != null ? String(v).trim() : '';
			if (t && !seen.has(t)) {
				seen.add(t);
				out.push(t);
			}
		};
		add(c?.termsindicator);
		if (Array.isArray(c?.details)) {
			for (const d of c.details) {
				if (!d?.name) {
					continue;
				}
				add(d?.payterms);
			}
		}
		if (out.length === 0) {
			add(this.getTermsFilterKey(c));
		}
		return out;
	}

	/** Severity coloring (90/120 day columns) only kicks in once a bucket actually owes money. */
	isPositiveAmount(v: any): boolean {
		return (parseFloat(v) || 0) > 0.0001;
	}

	/** Summary column: unique codes from open lines, e.g. (COD, PPD). */
	customerTermsSummary(row: any): string {
		const v = row?.terms_codes_display != null ? String(row.terms_codes_display).trim() : '';
		if (v) {
			return v;
		}
		return row?.terms != null ? String(row.terms) : '';
	}

	private pickDetailDateRaw(det: any, keyHints: readonly string[]): string | null {
		if (!det || typeof det !== 'object') {
			return null;
		}
		const o = det as Record<string, unknown>;
		const keyByLower = new Map<string, string>();
		for (const k of Object.keys(o)) {
			keyByLower.set(k.toLowerCase(), k);
		}
		for (const hint of keyHints) {
			const actual = keyByLower.get(hint.toLowerCase());
			if (!actual) {
				continue;
			}
			const v = o[actual];
			if (v != null && String(v).trim() !== '') {
				return String(v).trim();
			}
		}
		return null;
	}

	private formatDetailDateFromRaw(raw: string | null): string {
		if (!raw) {
			return '—';
		}
		const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
		const d = iso ? new Date(iso[1] + 'T12:00:00') : new Date(raw);
		if (Number.isNaN(d.getTime())) {
			return raw;
		}
		return this.datePipe.transform(d, 'shortDate') || raw;
	}

	formatDetailTranDate(det: any): string {
		const raw = this.pickDetailDateRaw(det, ['trandate', 'transdate', 'tran_date']);
		return this.formatDetailDateFromRaw(raw);
	}

	formatDetailDueDate(det: any): string {
		const raw = this.pickDetailDateRaw(det, ['duedate', 'due_date']);
		return this.formatDetailDateFromRaw(raw);
	}

	/** Payment terms indicator (code) from the AR line — not the long description. */
	detailTermCode(det: any): string {
		if (!det || typeof det !== 'object') {
			return '—';
		}
		for (const k of ['payterms', 'termsindicator', 'term_code']) {
			const v = det[k];
			if (v != null && String(v).trim() !== '') {
				return String(v).trim();
			}
		}
		return '—';
	}

	private buildFilterOptions(): void {
		const salesMap = new Map<string, string>();
		const catalog = Array.isArray(this.salesmenCatalog) ? this.salesmenCatalog : [];
		for (const s of catalog) {
			const code = s?.code != null ? String(s.code).trim() : '';
			if (!code) {
				continue;
			}
			const name = s?.name != null ? String(s.name).trim() : '';
			salesMap.set(code, name || code);
		}
		for (const c of this.masterData) {
			const code = this.getSalesmanCode(c);
			if (!code) {
				continue;
			}
			const name = (c?.salesmanname != null ? String(c.salesmanname) : '').trim();
			if (!salesMap.has(code)) {
				salesMap.set(code, name || code);
			} else if (name && salesMap.get(code) === code) {
				salesMap.set(code, name);
			}
		}
		this.salesmanOptions = Array.from(salesMap.entries())
			.map(([code, name]) => ({ code, name }))
			.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

		const termsMap = new Map<string, string>();
		for (const c of this.masterData) {
			if (Array.isArray(c?.details)) {
				for (const d of c.details) {
					if (!d?.name) {
						continue;
					}
					const pc = d?.payterms != null ? String(d.payterms).trim() : '';
					if (!pc) {
						continue;
					}
					if (termsMap.has(pc)) {
						continue;
					}
					const long = d?.terms != null ? String(d.terms).trim() : '';
					termsMap.set(pc, long && long !== pc ? `${pc} (${long})` : pc);
				}
			}
			const ti = c?.termsindicator != null ? String(c.termsindicator).trim() : '';
			if (ti && !termsMap.has(ti)) {
				const longM = c?.terms != null ? String(c.terms).trim() : '';
				termsMap.set(ti, longM && longM !== ti ? `${ti} (${longM})` : ti);
			}
			const fallback = this.getTermsFilterKey(c);
			if (fallback && !termsMap.has(fallback)) {
				const label = (c?.terms != null ? String(c.terms).trim() : '') || fallback;
				termsMap.set(fallback, label);
			}
		}
		this.termsOptions = Array.from(termsMap.entries())
			.map(([code, label]) => ({ code, label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

		this.buildBrandOptions();
	}

	/**
	 * Only brands actually sitting in the loaded AR lines — a rep chasing overdue money
	 * should not scroll past 40 brands that owe nothing. Falls back to the API catalog
	 * for naming. "(No brand)" is offered whenever some open line is untagged, so
	 * filtering can never make money disappear off the report unnoticed.
	 */
	private buildBrandOptions(): void {
		const names = new Map<string, string>();
		for (const b of Array.isArray(this.brandsCatalog) ? this.brandsCatalog : []) {
			const id = b?.id != null ? String(b.id).trim() : '';
			if (id) {
				names.set(id, (b?.name != null ? String(b.name).trim() : '') || id);
			}
		}
		const present = new Map<string, string>();
		let anyUntagged = false;
		for (const c of this.masterData) {
			if (!Array.isArray(c?.details)) {
				continue;
			}
			for (const d of c.details) {
				if (!d?.name) {
					continue;
				}
				const ids = Array.isArray(d?.brand_ids) ? d.brand_ids : [];
				if (ids.length === 0) {
					anyUntagged = true;
					continue;
				}
				const labels = Array.isArray(d?.brands) ? d.brands : [];
				ids.forEach((raw: unknown, i: number) => {
					const id = raw != null ? String(raw).trim() : '';
					if (!id || present.has(id)) {
						return;
					}
					const inline = labels[i]?.name != null ? String(labels[i].name).trim() : '';
					present.set(id, inline || names.get(id) || id);
				});
			}
		}
		const opts = Array.from(present.entries())
			.map(([code, label]) => ({ code, label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
		if (opts.length && anyUntagged) {
			opts.push({ code: TrialBalanceComponent.NO_BRAND_KEY, label: '(No brand)' });
		}
		this.brandOptions = opts;
	}

	private rowMatchesFilters(customer: any): boolean {
		if (!this.includeNegativeBalances && (parseFloat(customer.balance) < 0)) {
			return false;
		}
		if (this.cardOnFileFilter !== 'all') {
			const raw = customer?.card_on_file != null ? String(customer.card_on_file).trim().toUpperCase() : '';
			const hasCard = raw === 'Y';
			if (this.cardOnFileFilter === 'yes' && !hasCard) {
				return false;
			}
			if (this.cardOnFileFilter === 'no' && hasCard) {
				return false;
			}
		}
		if (this.filterSalesmanCodes.length > 0) {
			const sc = this.getSalesmanCode(customer);
			if (!this.filterSalesmanCodes.includes(sc)) {
				return false;
			}
		}
		if (this.filterTermsCodes.length > 0) {
			const keys = this.termFilterKeysForCustomer(customer);
			if (!keys.some((k) => this.filterTermsCodes.includes(k))) {
				return false;
			}
		}
		if (this.filterBrandIds.length > 0) {
			const keys = this.brandFilterKeysForCustomer(customer);
			if (!keys.some((k) => this.filterBrandIds.includes(k))) {
				return false;
			}
		}
		return true;
	}

	applyFilters(): void {
		this.all = this.masterData.filter((c) => this.rowMatchesFilters(c));
		this.applySearchOverlay();
	}

	/** Build `gridRows` from `all` and the search box; do not write to `@Input() data` (Angular resets inputs from parent). */
	private applySearchOverlay(): void {
		const v = this.toolbarSearchText ?? '';
		if (v.trim() !== '') {
			this.gridRows = this.globalSearchService.filterItem(
				this.all,
				v,
				'name,debtorno,terms,terms_codes_display,termsindicator,brands_display,salesman,salesmanname,card_on_file,typename,balance,future,current,due,overdue,overdue1,overdue2',
			);
		} else {
			this.gridRows = Array.isArray(this.all) ? this.all : [];
		}
		this.updateTotals();
		this.matdata = new MatTableDataSource(this.gridRows);
		this.attachSortAndPaginator();
		this.applySortToData();
		this.pruneStaleExpandedRow();
		this.savePersistedFilters();
	}

	/** `true` when this row has printable detail lines (same filter as the nested table). */
	hasExpandableLineDetails(row: any): boolean {
		return this.visibleDetailLines(row).length > 0;
	}

	/**
	 * Lines shown in the drill-down. With a brand filter on, this narrows to that
	 * brand's documents — that IS the answer to "what does this store owe us?" — so
	 * the lines deliberately no longer sum to the row's whole-customer balance. The
	 * panel header says so.
	 */
	visibleDetailLines(row: any): any[] {
		if (!row || typeof row !== 'object') {
			return [];
		}
		// Called from the template on every change-detection pass (and once per row in
		// Detail view, where the grid can hold thousands). Memoise against the filter
		// signature so *ngFor keeps the same array identity and does not re-render.
		const sig = this.filterBrandIds.join(',');
		if (row.__tbLineSig === sig && Array.isArray(row.__tbLines)) {
			return row.__tbLines;
		}
		const details = Array.isArray(row.details) ? row.details.filter((d: any) => d?.name) : [];
		const lines = this.filterBrandIds.length === 0
			? details
			: details.filter((d: any) => {
				const ids = Array.isArray(d?.brand_ids) ? d.brand_ids : [];
				if (ids.length === 0) {
					return this.filterBrandIds.includes(TrialBalanceComponent.NO_BRAND_KEY);
				}
				return ids.some((id: unknown) => this.filterBrandIds.includes(String(id).trim()));
			});
		row.__tbLineSig = sig;
		row.__tbLines = lines;
		return lines;
	}

	/** Balance of just the lines on screen — the number to read out on a brand's call. */
	visibleDetailBalance(row: any): number {
		return this.visibleDetailLines(row).reduce((acc, d) => acc + (parseFloat(d?.balance) || 0), 0);
	}

	get brandFilterActive(): boolean {
		return this.filterBrandIds.length > 0;
	}

	/** Nested lines: every row in Detail view; one row at a time in Summary (via ▸). */
	detailLinesVisibleForRow(row: any): boolean {
		return this.hasExpandableLineDetails(row) && (!this.summary_details.value || this.isRowDetailExpanded(row));
	}

	detailExpandRowKey(row: any): string {
		const d = row?.debtorno != null ? String(row.debtorno).trim() : '';
		const b = this.resolveBranchcodeForRow(row) || '';
		return `${d}\u001f${b}`;
	}

	isRowDetailExpanded(row: any): boolean {
		return this.expandedDetailRowKey !== null && this.detailExpandRowKey(row) === this.expandedDetailRowKey;
	}

	toggleRowDetailExpand(row: any, ev?: Event): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}
		if (!this.summary_details.value) {
			return;
		}
		if (!this.hasExpandableLineDetails(row)) {
			return;
		}
		const k = this.detailExpandRowKey(row);
		this.expandedDetailRowKey = this.isRowDetailExpanded(row) ? null : k;
	}

	private pruneStaleExpandedRow(): void {
		if (!this.summary_details.value || !this.expandedDetailRowKey) {
			return;
		}
		const rows = Array.isArray(this.gridRows) ? this.gridRows : [];
		const stillVisible = rows.some(
			(r) => this.detailExpandRowKey(r) === this.expandedDetailRowKey && this.hasExpandableLineDetails(r),
		);
		if (!stillVisible) {
			this.expandedDetailRowKey = null;
		}
	}

	private attachSortAndPaginator(): void {
		if (!this.matdata) {
			return;
		}
		if (this.paginator) {
			this.matdata.paginator = this.paginator;
		}
	}

	updateTotals() {
		const rows = Array.isArray(this.gridRows) ? this.gridRows : [];
		this.total_balance = rows.reduce((acc, c) => acc + (parseFloat(c?.balance) || 0), 0);
		this.total_future = rows.reduce((acc, c) => acc + (parseFloat(c?.future) || 0), 0);
		this.total_current = rows.reduce((acc, c) => acc + (parseFloat(c?.current) || 0), 0);
		this.total_due = rows.reduce((acc, c) => acc + (parseFloat(c?.due) || 0), 0);
		this.total_overdue = rows.reduce((acc, c) => acc + (parseFloat(c?.overdue) || 0), 0);
		this.total_overdue1 = rows.reduce((acc, c) => acc + (parseFloat(c?.overdue1) || 0), 0);
		this.total_overdue2 = rows.reduce((acc, c) => acc + (parseFloat(c?.overdue2) || 0), 0);
	}

	onToolbarSearchChange(): void {
		this.applySearchOverlay();
	}

	setSort(column: string): void {
		if (!column) {
			return;
		}
		if (this.sortColumn === column) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDir = 'asc';
		}
		this.applySortToData();
		this.savePersistedFilters();
	}

	sortIndicator(column: string): 'asc' | 'desc' | null {
		return this.sortColumn === column ? this.sortDir : null;
	}

	private applySortToData(): void {
		if (!this.sortColumn || !Array.isArray(this.gridRows) || this.gridRows.length === 0) {
			return;
		}
		const col = this.sortColumn;
		const mul = this.sortDir === 'asc' ? 1 : -1;
		this.gridRows = [...this.gridRows].sort((a, b) => mul * this.compareSortRows(a, b, col));
		this.matdata = new MatTableDataSource(this.gridRows);
		this.attachSortAndPaginator();
	}

	private compareSortRows(a: any, b: any, col: string): number {
		if (col === 'salesperson') {
			const sa = this.salespersonDisplay(a).toLowerCase();
			const sb = this.salespersonDisplay(b).toLowerCase();
			return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
		}
		if (col === 'terms') {
			const ta = String(a?.terms_codes_display ?? a?.terms ?? '')
				.trim()
				.toLowerCase();
			const tb = String(b?.terms_codes_display ?? b?.terms ?? '')
				.trim()
				.toLowerCase();
			return ta.localeCompare(tb, undefined, { sensitivity: 'base' });
		}
		if (this.sortNumericKeys.has(col)) {
			const na = parseFloat(a?.[col]) || 0;
			const nb = parseFloat(b?.[col]) || 0;
			if (na === nb) {
				return 0;
			}
			return na < nb ? -1 : 1;
		}
		let sa = a?.[col];
		let sb = b?.[col];
		sa = sa != null ? String(sa).trim().toLowerCase() : '';
		sb = sb != null ? String(sb).trim().toLowerCase() : '';
		return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
	}

	exportPdf() {
		this.sending = true;
		this.paginator_show = false;
		const today = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.printTableRed.nativeElement.innerHTML);
		const data = {
			content: encoded,
			filename: 'ar_balance',
			landscape: true,
			title: 'Customer Trial Balance',
			subtitle: today.toDateString(),
		}

		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.sending = false;
		});
	}

	/**
	 * HTML→spreadsheet conversion reads text nodes; strip mat-icon ligatures and non-print UI
	 * (Lines / Account / Pay / detail chrome) so exports match on-screen data columns.
	 */
	private buildSpreadsheetExportHtml(): string {
		const clone = this.printTableRed.nativeElement.cloneNode(true) as HTMLElement;
		clone.querySelectorAll('.donotprint').forEach((n) => n.remove());
		clone.querySelectorAll('mat-icon, .mat-icon, i.material-icons').forEach((n) => n.remove());
		return clone.innerHTML;
	}

	exportXls() {
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.buildSpreadsheetExportHtml());

		const today = new Date();

		const data = {
			content: encoded,
			filename: 'ar_balance',
			title: 'Customer Trial Balance',
			subtitle: today.toDateString(),
		}

		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.sending = false;
		});
	}

	emitAccountOverview(row: any, ev?: Event): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}
		const payload = this.customerActionPayload(row);
		if (!payload) {
			return;
		}
		this.accountOverview.emit(payload);
	}

	emitTakePayment(row: any, ev?: Event): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}
		const payload = this.customerActionPayload(row);
		if (!payload) {
			return;
		}
		this.takePayment.emit(payload);
	}

	private customerActionPayload(row: any): { debtorno: string; branchcode: string; title: string } | null {
		const debtorno = row?.debtorno != null ? String(row.debtorno).trim() : '';
		const branchcode = this.resolveBranchcodeForRow(row);
		if (!debtorno || !branchcode) {
			return null;
		}
		const name = row?.name != null ? String(row.name).trim() : '';
		const title = name ? `${name} · ${debtorno}.${branchcode}` : `${debtorno}.${branchcode}`;
		return { debtorno, branchcode, title };
	}

	emitToolbarRefresh(): void {
		this.toolbarRefresh.emit();
	}

	/** Query params merged into customer view links from the grid (tab + branch when known). */
	customerViewQueryForRow(row: any): Record<string, string> {
		const out: Record<string, string> = {};
		const tab = this.customerViewTab != null ? String(this.customerViewTab).trim() : '';
		if (tab) {
			out.tab = tab;
		}
		const bc = this.resolveBranchcodeForRow(row);
		if (bc) {
			out.branch = bc;
		}
		return out;
	}

	/** Main grid: salesman label (API `salesman` / `salesmanname`). */
	salespersonDisplay(row: any): string {
		const name = row?.salesmanname != null ? String(row.salesmanname).trim() : '';
		const code = row?.salesman != null ? String(row.salesman).trim() : '';
		if (name && code) {
			return `${name} (${code})`;
		}
		if (name) {
			return name;
		}
		if (code) {
			return code;
		}
		return '—';
	}

	private resolveBranchcodeForRow(row: any): string {
		const direct = row?.branchcode != null ? String(row.branchcode).trim() : '';
		if (direct) {
			return direct;
		}
		const details = row?.details;
		if (!Array.isArray(details)) {
			return '';
		}
		for (const d of details) {
			const bc = d?.branchcode != null ? String(d.branchcode).trim() : '';
			if (bc) {
				return bc;
			}
		}
		return '';
	}

}
